import { useCallback, useContext } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'

import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import noop from 'lib/function/noop'
import { getCurrentDateLabel } from 'selectors/offerPage/offerPageSelectors'

import OfferFilterSelectChip from '../OfferFilterSelectChip'

interface Props {
  dateLabel: string
  isOpen: boolean
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  className?: string
  ref?: React.Ref<HTMLButtonElement>
}

/**
 * Chip trigger for the date filter dropdowns — the `chip` trigger variant of
 * `OfferDatePickerSelect` and `OfferCalendarDatePickerSelect`. It shows the committed
 * dates (or "Anytime"), so unlike `OfferDatePickerSelectBox` it doesn't follow an
 * in-progress selection while the picker is open.
 */
function OfferDateSelectChip(props: Props) {
  const { dateLabel, isOpen, onClick = noop, className, ref } = props
  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const onChipClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    if (isOpen) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('CALENDAR_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      trackEvent('CALENDAR_TOGGLE_PRESSED')
      onClick(e)
    }
  }, [dispatch, isOpen, onClick, trackEvent])

  return (
    <OfferFilterSelectChip
      ref={ref}
      className={className}
      size="medium"
      selected={isOpen}
      startIcon={<LineCalendarIcon />}
      onClick={onChipClick}
      data-testid="OfferDatePickerSelectBox"
    >
      {dateLabel || intl.formatMessage({
        id: 'GbOVEdKRuFUe9uvuqy65',
        defaultMessage: 'Anytime',
        description: 'Label on the date filter chip on the offer page when the traveller has not chosen any dates yet',
      })}
    </OfferFilterSelectChip>
  )
}

type MappedProps = Pick<Props, 'dateLabel' | 'isOpen'>

export default withOfferPageState((state): MappedProps => ({
  dateLabel: getCurrentDateLabel(state),
  isOpen: state.activeFilter === 'checkin',
}), OfferDateSelectChip)
